import { INTRUSION_PARTITION_STATUSES, INTRUSION_ZONE_STATUSES } from '@/views/intrusion/constants';
import type {
  IntrusionPartitionStatusFlag,
  IntrusionZoneStatusFlag,
  ZonePartitionArmState
} from '@/views/intrusion/types';

export type StatusCellVariant =
  | 'normal'
  | 'armed'
  | 'attention'
  | 'transitional'
  | 'trouble'
  | 'violated'
  | 'alarm'
  | 'disarmed'
  | 'syncing';

export type { ZonePartitionArmState };

type StatusValue = IntrusionPartitionStatusFlag | IntrusionZoneStatusFlag;

export interface StatusCellVariantOptions {
  /** Only consulted for zone statuses; partitions carry their own arm state in `status`. */
  partitionArmState?: ZonePartitionArmState;
}

const STATUS_VARIANT_MAP: Partial<Record<StatusValue, StatusCellVariant>> = {
  // INTRUSION_PARTITION_STATUSES.ALARM is the same 'Alarm' literal, so this one entry colours both.
  [INTRUSION_ZONE_STATUSES.ALARM]: 'alarm',
  [INTRUSION_ZONE_STATUSES.TROUBLE]: 'trouble',
  [INTRUSION_PARTITION_STATUSES.EXIT_DELAY]: 'transitional',
  [INTRUSION_PARTITION_STATUSES.ENTRY_DELAY]: 'transitional',
  [INTRUSION_PARTITION_STATUSES.NOT_READY]: 'attention',
  [INTRUSION_ZONE_STATUSES.OPEN]: 'attention',
  [INTRUSION_PARTITION_STATUSES.ARMED_STAY]: 'armed',
  [INTRUSION_PARTITION_STATUSES.ARMED_AWAY]: 'armed',
  [INTRUSION_PARTITION_STATUSES.ARMED_NIGHT]: 'armed',
  [INTRUSION_ZONE_STATUSES.BYPASSED]: 'armed',
  [INTRUSION_PARTITION_STATUSES.READY]: 'normal',
  [INTRUSION_ZONE_STATUSES.CLOSED]: 'normal',
  [INTRUSION_PARTITION_STATUSES.DISARMED]: 'normal'
};

const STATUS_VARIANT_PRIORITY: readonly StatusCellVariant[] = [
  'alarm',
  'trouble',
  'transitional',
  'armed',
  'violated',
  'attention',
  'normal'
];

/** An open zone is a violation only on a settled armed partition — never mid exit/entry delay. */
const isViolatedZone = (
  status: readonly StatusValue[],
  partitionArmState: ZonePartitionArmState
): boolean => partitionArmState === 'armed' && status.includes(INTRUSION_ZONE_STATUSES.OPEN);

export const getStatusCellVariant = (
  isSyncing: boolean,
  status: readonly StatusValue[],
  { partitionArmState = 'unknown' }: StatusCellVariantOptions = {}
): StatusCellVariant => {
  if (isSyncing) return 'syncing';

  const matchedVariants = new Set(
    status
      .map((value) => STATUS_VARIANT_MAP[value])
      .filter((variant): variant is StatusCellVariant => variant !== undefined)
  );

  if (isViolatedZone(status, partitionArmState)) {
    matchedVariants.delete('attention');
    matchedVariants.add('violated');
  }

  return STATUS_VARIANT_PRIORITY.find((variant) => matchedVariants.has(variant)) ?? 'disarmed';
};
